import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { api } from '../../services/api';
import { MarketingBrand } from '../../types';
import { CreditCostNotice } from '../credits/CreditCostNotice';
import { useCreditConfirm } from '../credits/useCreditConfirm';

interface Props {
  brand: MarketingBrand | null;
}

const emptyForm = {
  platform: 'instagram',
  handle: '',
  url: '',
  displayName: '',
  classification: 'copiar',
  reactionPermission: 'PENDENTE',
  notes: '',
};

const classifications = [
  ['copiar', 'Copiar'],
  ['adaptar', 'Adaptar'],
  ['inspirar', 'Inspirar'],
  ['contrastar', 'Contrastar'],
  ['aprofundar', 'Aprofundar'],
  ['pendente', 'Pendente'],
];

const classificationColors: Record<string, string> = {
  'copiar': 'bg-rose-900/50 text-rose-300 border-rose-700/50',
  'adaptar': 'bg-orange-900/50 text-orange-300 border-orange-700/50',
  'inspirar': 'bg-indigo-900/50 text-indigo-300 border-indigo-700/50',
  'contrastar': 'bg-purple-900/50 text-purple-300 border-purple-700/50',
  'aprofundar': 'bg-emerald-900/50 text-emerald-300 border-emerald-700/50',
  'pendente': 'bg-slate-800 text-slate-400 border-slate-700',
};

const classificationDescriptions: Record<string, string> = {
  'copiar': 'Replica a estrutura 1:1 - mesmo hook, mesmo formato, mesmo ritmo',
  'adaptar': 'Traduz o mecanismo para o tom/publico da marca',
  'inspirar': 'Cria algo novo usando apenas o gatilho emocional como ponto de partida',
  'contrastar': 'Apresenta o angulo oposto com a mesma intensidade',
  'aprofundar': 'Expande um ponto do original com mais profundidade ou dados',
};

function normalizeHandle(value: string) {
  return value.trim().replace(/^@/, '').replace(/^https?:\/\/(www\.)?instagram\.com\//i, '').replace(/^https?:\/\/(www\.)?tiktok\.com\/@?/i, '').replace(/\/$/, '');
}

function profileInputFromProfile(profile: any) {
  return profile.url || (profile.handle ? `@${profile.handle}` : '');
}

function normalizeReactionValue(value: string) {
  return value === 'NUNCA' ? 'NAO' : (value || 'PENDENTE');
}

function fmtNum(n: any) {
  const v = Number(n);
  if (!v) return null;
  if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`;
  if (v >= 1_000) return `${(v / 1_000).toFixed(0)}k`;
  return String(v);
}

export function InspirationProfilesView({ brand }: Props) {
  const [profiles, setProfiles] = useState<any[]>([]);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [discovering, setDiscovering] = useState(false);
  const { request: requestDiscoverConfirm, modal: discoverConfirmModal } = useCreditConfirm();
  const [keywords, setKeywords] = useState('');
  const [platforms, setPlatforms] = useState<string[]>(['instagram', 'tiktok']);
  const [quickSaving, setQuickSaving] = useState<string | null>(null);
  const [showDiscoverModal, setShowDiscoverModal] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);

  const pending = profiles.filter(p => p.classification === 'pendente');
  const classified = profiles.filter(p => p.classification !== 'pendente');

  const load = async () => {
    if (!brand) return;
    setLoading(true);
    try {
      const res = await api.getMarketingInspirations(brand.id);
      setProfiles(res.inspirations || []);
    } catch (error: any) {
      toast.error(error.message || 'Erro ao carregar perfis');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [brand?.id]);

  const reset = () => {
    setEditingId(null);
    setForm(emptyForm);
  };

  const openCreateProfile = () => {
    reset();
    setShowProfileModal(true);
  };

  const save = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!brand) return;
    const profile = form.handle.trim();
    if (!profile) return toast.error('Informe o @ ou URL do perfil.');
    setSaving(true);
    try {
      const payload = { ...form, profile, handle: profile, url: undefined, displayName: undefined };
      if (editingId) {
        const res = await api.updateMarketingInspiration(brand.id, editingId, payload);
        setProfiles(prev => prev.map(p => p.id === editingId ? res.inspiration : p));
        toast.success('Perfil atualizado');
      } else {
        const res = await api.createMarketingInspiration(brand.id, payload);
        setProfiles(prev => [res.inspiration, ...prev]);
        toast.success('Perfil cadastrado');
      }
      reset();
      setShowProfileModal(false);
    } catch (error: any) {
      toast.error(error.message || 'Erro ao salvar perfil');
    } finally {
      setSaving(false);
    }
  };

  const quickClassify = async (profile: any, classification: string) => {
    if (!brand) return;
    setQuickSaving(profile.id);
    try {
      const payload = { ...profile, classification };
      const res = await api.updateMarketingInspiration(brand.id, profile.id, payload);
      setProfiles(prev => prev.map(p => p.id === profile.id ? res.inspiration : p));
    } catch (error: any) {
      toast.error(error.message || 'Erro ao classificar');
    } finally {
      setQuickSaving(null);
    }
  };

  const quickReactionPermission = async (profile: any, reactionPermission: string) => {
    if (!brand) return;
    setQuickSaving(profile.id);
    try {
      const res = await api.updateMarketingInspiration(brand.id, profile.id, { ...profile, reactionPermission });
      setProfiles(prev => prev.map(p => p.id === profile.id ? res.inspiration : p));
    } catch (error: any) {
      toast.error(error.message || 'Erro ao atualizar reaction');
    } finally {
      setQuickSaving(null);
    }
  };

  const edit = (profile: any) => {
    setEditingId(profile.id);
    setForm({
      platform: profile.platform || 'instagram',
      handle: profileInputFromProfile(profile),
      url: '',
      displayName: '',
      classification: profile.classification || 'inspiracao',
      reactionPermission: normalizeReactionValue(profile.reactionPermission),
      notes: profile.notes || '',
    });
    setShowProfileModal(true);
  };

  const remove = async (profile: any) => {
    if (!brand || !window.confirm(`Remover @${profile.handle}?`)) return;
    try {
      await api.deleteMarketingInspiration(brand.id, profile.id);
      setProfiles(prev => prev.filter(p => p.id !== profile.id));
      toast.success('Perfil removido');
    } catch (error: any) {
      toast.error(error.message || 'Erro ao remover perfil');
    }
  };

  const discover = async () => {
    if (!brand) return;
    if (!keywords.trim()) return toast.error('Informe palavras-chave do nicho.');
    if (!platforms.length) return toast.error('Selecione pelo menos uma plataforma.');
    setDiscovering(true);
    try {
      const res = await api.discoverMarketingInspirations(brand.id, {
        keywords: keywords.split(/[,.\n]/).map(k => k.trim()).filter(Boolean),
        platforms,
        limit: 30,
      });
      if (!res.apifyAvailable) toast.error(res.message || 'Apify nao configurado');
      else toast.success(res.message || 'Busca concluida');
      await load();
      setShowDiscoverModal(false);
    } catch (error: any) {
      toast.error(error.message || 'Erro na busca do agente');
    } finally {
      setDiscovering(false);
    }
  };

  if (!brand) {
    return (
      <div className="mx-auto max-w-3xl rounded-2xl border border-slate-800 bg-slate-950 p-8 text-center text-slate-400">
        Crie a Brand antes de cadastrar perfis de inspiracao.
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-5xl space-y-6 rounded-2xl border border-slate-800 bg-slate-950 p-6 shadow-sm">
      {discoverConfirmModal}
      <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h2 className="text-lg font-bold text-slate-100">Perfis de inspiracao</h2>
          <p className="text-sm text-slate-400">Benchmarks, criadores, concorrentes e fontes que alimentam pesquisa viral e briefs.</p>
        </div>
        <div className="flex gap-3 text-xs font-semibold uppercase tracking-widest text-slate-500">
          <span>{pending.length} para revisar</span>
          <span>·</span>
          <span>{classified.length} cadastrado(s)</span>
        </div>
      </div>

      <div className="flex flex-wrap gap-3">
        <button onClick={() => setShowDiscoverModal(true)} className="rounded-lg bg-indigo-700 px-4 py-2 text-sm font-bold text-white hover:bg-indigo-600">
          Agente de descoberta
        </button>
        <button onClick={openCreateProfile} className="rounded-lg border border-slate-700 bg-slate-900 px-4 py-2 text-sm font-bold text-slate-100 hover:bg-slate-800">
          Cadastrar perfil manualmente
        </button>
      </div>

      {showDiscoverModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm">
          <section className="w-full max-w-2xl space-y-3 rounded-xl border border-slate-800 bg-slate-900 p-5 shadow-2xl">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-sm font-bold text-slate-100">Agente de descoberta</p>
                <p className="mt-1 text-xs text-slate-500">Busca posts em alta por hashtag, ordena por engajamento e salva os melhores perfis para revisao.</p>
              </div>
              <button onClick={() => setShowDiscoverModal(false)} className="rounded-lg px-2 py-1 text-sm text-slate-400 hover:bg-slate-800">Fechar</button>
            </div>

            <input value={keywords} onChange={e => setKeywords(e.target.value)} placeholder="Palavras-chave separadas por virgula - ex: estetica automotiva, skincare" className="w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-100 placeholder:text-slate-600" />

            <div className="flex flex-wrap items-center gap-4">
              <div className="flex items-center gap-3">
                {[[ 'instagram', 'Instagram' ], [ 'tiktok', 'TikTok' ]].map(([val, label]) => (
                  <label key={val} className="flex cursor-pointer items-center gap-1.5 text-xs text-slate-300">
                    <input type="checkbox" checked={platforms.includes(val)} onChange={e => setPlatforms(prev => e.target.checked ? [...prev, val] : prev.filter(p => p !== val))} className="accent-indigo-500" />
                    {label}
                  </label>
                ))}
              </div>

              <button onClick={() => requestDiscoverConfirm({
                title: 'Agente de descoberta de perfis',
                items: [{ featureKey: 'discovery_search', qty: 1 }],
                note: 'Cobrança única por busca (varre Instagram e TikTok e salva até 30 perfis).',
                confirmLabel: 'Buscar',
                onConfirm: discover,
              })} disabled={discovering} className="ml-auto rounded-lg bg-indigo-700 px-4 py-2 text-sm font-bold text-white hover:bg-indigo-600 disabled:opacity-50">
                {discovering ? 'Buscando...' : 'Buscar'}
              </button>
            </div>

            <p className="text-[10px] text-slate-600">Coleta 30 posts por plataforma por palavra-chave. Buscas estimadas: {platforms.length * (keywords.split(/[,\.\n]/).filter(k => k.trim()).length || 0)}.</p>

            <CreditCostNotice
              items={[{ featureKey: 'discovery_search', qty: 1 }]}
              note="Cobrança única por busca (varre Instagram e TikTok e salva até 30 perfis)."
            />
          </section>
        </div>
      )}

      {showProfileModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm">
          <form onSubmit={save} className={`grid w-full max-w-2xl gap-3 rounded-xl border p-5 shadow-2xl md:grid-cols-2 ${editingId ? 'border-indigo-500/50 bg-slate-900' : 'border-slate-800 bg-slate-900'}`}>
            <div className="md:col-span-2 flex items-center justify-between">
              <p className="text-xs font-bold uppercase tracking-widest text-slate-500">
                {editingId ? <span className="text-indigo-400">Editando perfil</span> : 'Cadastrar perfil manualmente'}
              </p>
              <button type="button" onClick={() => { reset(); setShowProfileModal(false); }} className="rounded-lg px-2 py-1 text-sm text-slate-400 hover:bg-slate-800">Fechar</button>
            </div>
            <input value={form.handle} onChange={e => setForm(f => ({ ...f, handle: e.target.value }))} placeholder="@perfil ou URL do Instagram/TikTok" className="rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-100 placeholder:text-slate-600 md:col-span-2" />
            <p className="-mt-2 text-[10px] text-slate-500 md:col-span-2">Cole a URL do Instagram/TikTok ou informe @perfil. Se usar apenas @, o sistema salva como Instagram.</p>
            <select value={form.classification} onChange={e => setForm(f => ({ ...f, classification: e.target.value }))} className="rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-100">
              {classifications.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
            </select>
            <select value={form.reactionPermission} onChange={e => setForm(f => ({ ...f, reactionPermission: e.target.value }))} className="rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-100">
              <option value="SIM">Reaction: SIM</option><option value="NAO">Reaction: NAO</option><option value="PENDENTE">Reaction: PENDENTE</option>
            </select>
            <textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} placeholder="O que observar, o que evitar copiar e oportunidade" className="min-h-20 rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-100 placeholder:text-slate-600 md:col-span-2" />
            <div className="flex justify-end gap-2 md:col-span-2">
              <button type="button" onClick={() => { reset(); setShowProfileModal(false); }} className="rounded-lg px-4 py-2 text-sm text-slate-400 hover:bg-slate-800">Cancelar</button>
              <button disabled={saving} className="rounded-lg bg-slate-800 px-4 py-2 text-sm font-bold text-slate-100 hover:bg-slate-700 disabled:opacity-50">
                {saving ? 'Salvando...' : editingId ? 'Salvar edicao' : 'Cadastrar perfil'}
              </button>
            </div>
          </form>
        </div>
      )}
      {/* Lista: Para revisar (pendente) */}
      {loading ? (
        <p className="text-sm text-slate-500">Carregando...</p>
      ) : (
        <>
          {pending.length > 0 && (
            <section>
              <div className="mb-3 flex items-center gap-2">
                <span className="h-2 w-2 rounded-full bg-amber-400" />
                <h3 className="text-sm font-bold text-slate-100">Para revisar</h3>
                <span className="rounded-full bg-amber-900/40 px-2 py-0.5 text-[10px] font-bold text-amber-400">{pending.length}</span>
              </div>
              <div className="grid gap-3 md:grid-cols-2">
                {pending.map(profile => {
                  const likes = profile.metadata?.likes;
                  const comments = profile.metadata?.comments;
                  const keyword = profile.metadata?.keyword;
                  const isQuickSaving = quickSaving === profile.id;
                  return (
                    <article key={profile.id} className="rounded-xl border border-amber-900/30 bg-slate-900 p-4">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <p className="truncate font-bold text-slate-100">@{profile.handle}</p>
                          <p className="text-xs text-slate-500 capitalize">{profile.platform}{profile.displayName ? ` · ${profile.displayName}` : ''}</p>
                        </div>
                        <div className="flex shrink-0 flex-col items-end gap-1">
                          {fmtNum(profile.metadata?.views) && (
                            <span className="text-[10px] font-bold text-sky-400">Views {fmtNum(profile.metadata?.views)}</span>
                          )}
                          {fmtNum(likes) && (
                            <span className="text-[10px] font-bold text-emerald-400">Likes {fmtNum(likes)}</span>
                          )}
                          {fmtNum(comments) && (
                            <span className="text-[10px] text-slate-500">Com. {fmtNum(comments)}</span>
                          )}
                        </div>
                      </div>
                      {keyword && <p className="mt-1 text-[10px] text-slate-600">Buscado por: {keyword}</p>}
                      {profile.url && (
                        <a href={profile.url} target="_blank" rel="noreferrer" className="mt-2 block truncate text-xs text-indigo-300 hover:underline">{profile.url}</a>
                      )}

                      {/* Classificacao rapida */}
                      <div className="mt-3">
                        <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-wider text-slate-500">Classificar como:</p>
                        <div className="flex flex-wrap gap-1.5">
                          {classifications.filter(([v]) => v !== 'pendente').map(([value, label]) => (
                            <button
                              key={value}
                              disabled={isQuickSaving}
                              onClick={() => quickClassify(profile, value)}
                              title={classificationDescriptions[value]}
                              className={`rounded-full border px-2 py-0.5 text-[10px] font-semibold transition-opacity hover:opacity-90 disabled:opacity-40 ${classificationColors[value] || 'bg-slate-800 text-slate-300 border-slate-700'}`}
                            >
                              {label}
                            </button>
                          ))}
                        </div>
                      </div>

                      <div className="mt-3">
                        <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-wider text-slate-500">Reaction:</p>
                        <select
                          value={normalizeReactionValue(profile.reactionPermission)}
                          onChange={e => quickReactionPermission(profile, e.target.value)}
                          disabled={isQuickSaving}
                          className="w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-xs font-semibold text-slate-100 disabled:opacity-50"
                        >
                          <option value="SIM">Reaction: SIM</option>
                          <option value="NAO">Reaction: NAO</option>
                          <option value="PENDENTE">Reaction: PENDENTE</option>
                        </select>
                      </div>

                      <div className="mt-3 flex justify-end gap-2">
                        <button onClick={() => edit(profile)} className="rounded-lg border border-slate-700 px-3 py-1.5 text-xs font-semibold text-slate-300 hover:bg-slate-800">Editar</button>
                        <button onClick={() => remove(profile)} className="rounded-lg border border-rose-900/70 px-3 py-1.5 text-xs font-semibold text-rose-300 hover:bg-rose-950/40">Remover</button>
                      </div>
                    </article>
                  );
                })}
              </div>
            </section>
          )}

          {/* Lista: Perfis cadastrados */}
          {classified.length > 0 && (
            <section>
              <div className="mb-3 flex items-center gap-2">
                <span className="h-2 w-2 rounded-full bg-emerald-400" />
                <h3 className="text-sm font-bold text-slate-100">Perfis cadastrados</h3>
                <span className="rounded-full bg-emerald-900/40 px-2 py-0.5 text-[10px] font-bold text-emerald-400">{classified.length}</span>
              </div>
              <div className="grid gap-3 md:grid-cols-2">
                {classified.map(profile => (
                  <article key={profile.id} className="rounded-xl border border-slate-800 bg-slate-900 p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="truncate font-bold text-slate-100">@{profile.handle}</p>
                        <p className="text-xs text-slate-500 capitalize">{profile.displayName || profile.platform}</p>
                      </div>
                      <span className={`shrink-0 rounded-full border px-2 py-0.5 text-[10px] font-bold ${classificationColors[profile.classification] || 'bg-slate-800 text-slate-300 border-slate-700'}`}>
                        {classifications.find(([v]) => v === profile.classification)?.[1] || profile.classification}
                      </span>
                    </div>
                    {profile.notes && <p className="mt-3 line-clamp-3 text-sm text-slate-400">{profile.notes}</p>}
                    {profile.url && <a href={profile.url} target="_blank" rel="noreferrer" className="mt-2 block truncate text-xs text-indigo-300 hover:underline">{profile.url}</a>}
                    <div className="mt-4 flex justify-end gap-2">
                      <button onClick={() => edit(profile)} className="rounded-lg border border-slate-700 px-3 py-1.5 text-xs font-semibold text-slate-300 hover:bg-slate-800">Editar</button>
                      <button onClick={() => remove(profile)} className="rounded-lg border border-rose-900/70 px-3 py-1.5 text-xs font-semibold text-rose-300 hover:bg-rose-950/40">Remover</button>
                    </div>
                  </article>
                ))}
              </div>
            </section>
          )}

          {profiles.length === 0 && (
            <p className="text-center text-sm text-slate-600">Nenhum perfil ainda. Use o agente de descoberta ou cadastre manualmente.</p>
          )}
        </>
      )}
    </div>
  );
}





